import {useState} from 'react';
import {useIntl} from 'react-intl';
import {
  Text,
  Center,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Heading,
  ModalFooter,
  Button,
  useToast,
  chakra,
  Box,
  Flex,
  ChakraStyledOptions,
} from '@chakra-ui/react';
import * as RadioGroup from '@radix-ui/react-radio-group';

import {useTranslate} from '@eksab/i18n';
import {PowerStats} from '@eksab/components';
import {RocketIcon} from '@eksab/assets/icons';

import {BatteryIcon} from '@eksab/features/store/badges/icons';
import type {MyPower, Power} from '@eksab/features/store/badges/types';
import {useConsumePowerMutation} from '@eksab/hooks/useConsumePowerMutation';

import {useInvalidateCompetition} from '../../hooks/useCompetitionQuery';

const RadioBox = {
  Root: chakra(RadioGroup.Root),
  Indicator: chakra(RadioGroup.Indicator),
  Item: chakra(RadioGroup.Item),
};

type BoostPrizeModalProps = {
  powers: MyPower[] | undefined;
  competitionEntryId: number;
  competitionId: number;
  isOpen: boolean;
  onClose: () => void;
};

export function BoostPrizeModal({powers, competitionEntryId, competitionId, isOpen, onClose}: BoostPrizeModalProps) {
  const t = useTranslate();
  const toast = useToast();
  const format = useIntl().formatNumber;

  const [selectedPower, setSelectedPower] = useState<number | undefined>(undefined);

  const consumePowerMutation = useConsumePowerMutation();

  const invalidateCompetition = useInvalidateCompetition();

  const onBoost = (id: number) => {
    return consumePowerMutation.mutate(
      {id, competition_entry_id: competitionEntryId},
      {
        onSuccess: () => {
          toast({title: t('prize.booster.boost.success'), status: 'success', id: 'boost-applied-success'});
          invalidateCompetition(competitionId, 'user');
          onClose();
        },

        onError: (e) => {
          toast({
            title: e?.response?.data?.message ?? t('app.errors.try-again'),
            status: 'error',
            id: 'boost-applied-error',
          });
        },
      },
    );
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} motionPreset="slideInBottom" variant="full" size="xs">
      <ModalOverlay />
      <ModalContent rounded="3xl" px="4">
        <ModalHeader></ModalHeader>
        <ModalBody display="flex" flexDirection="column" alignItems="center" p="0">
          <Center boxSize="52px" borderRadius="full" borderWidth="1px" bg="#2A2E3A" mb="4" borderColor="#FB6B02">
            <RocketIcon />
          </Center>
          <Text color="#8F8F8F" fontSize="12" fontWeight="bold">
            {t('prize.booster.boost')}
          </Text>
          <Heading fontSize="22px" fontWeight="700" color="black" mb="4" textAlign="center">
            {t('prize.booster.choose.booster.title')}
          </Heading>

          <Flex justify="space-between" mb="4" w="100%" align="center">
            <Flex flexDir="column">
              <Text fontSize="18px" fontWeight="700" color="#131318" mb="1">
                {t('store.packages.benefits.powers')}
              </Text>

              <Text fontSize="14px" fontWeight="600" me="1" color="#ABABAB">
                {t('prize.booster.choose.booster.body')}
              </Text>
            </Flex>

            <Text fontSize="14px" fontWeight="700" color="#2A2E3A">
              {format(powers?.length ?? 0)} {t('store.packages.card.powers')}
            </Text>
          </Flex>
          <RadioBox.Root
            display="flex"
            flexDir="column"
            w="100%"
            value={selectedPower?.toString()}
            onValueChange={(checked) => setSelectedPower(Number(checked))}
          >
            {powers?.map(({id, power}) => <PowerRadioBox power={power} key={id} value={id.toString()} />)}
          </RadioBox.Root>
        </ModalBody>
        <ModalFooter gap="3.5" justifyContent="space-between" px="0" pt="1.5" pos="sticky">
          <Button
            size="lg"
            bgColor="white"
            textColor="black"
            borderRadius="30px"
            borderColor="black"
            border="1px"
            pos="relative"
            fontWeight="700"
            onClick={onClose}
            _hover={{}}
            _active={{}}
            textTransform="capitalize"
          >
            {t('app.actions.cancel')}
          </Button>
          <Button
            textTransform="capitalize"
            px="14"
            py="4"
            fontWeight="700"
            onClick={() => onBoost(selectedPower as number)}
            _disabled={{bg: 'gray.400', cursor: 'not-allowed'}}
            _hover={{}}
            _active={{}}
            disabled={!selectedPower}
          >
            {t('prize.booster.boost.cta')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const powerRadioBoxActiveStyles: ChakraStyledOptions = {
  borderColor: 'green.400',
  color: '#2a2e3a',
  '.power-stats': {bgPos: 'left', color: 'white'},
};

const PowerRadioBox = ({power, ...props}: {power: Power} & RadioGroup.RadioGroupItemProps) => {
  return (
    <RadioBox.Item
      display="flex"
      alignItems="center"
      _checked={{...powerRadioBoxActiveStyles, shadow: 'inset 0 0 0 1px var(--chakra-colors-green-400)'}}
      _hover={powerRadioBoxActiveStyles}
      borderColor="#DFDFDF"
      borderWidth="1px"
      borderRadius="12px"
      color="#586486"
      p="2"
      w="100%"
      mb="2"
      transition="color 0.2s, background-position 0.25s ease-in"
      {...props}
    >
      <Center boxSize="6" borderColor="#DFDFDF" borderWidth="1px" rounded="full" me="2">
        <RadioBox.Indicator>
          <Box boxSize="4" bgColor="green.400" rounded="full" />
        </RadioBox.Indicator>
      </Center>

      <Flex flexDir="column" gap="1">
        <Flex
          w="fit-content"
          bgColor="#F3F6FF"
          px="2"
          py="1.5"
          rounded="0.5rem"
          gap="2"
          align="center"
          fontSize="sm"
          fontWeight="700"
        >
          <Box rounded="full" bgColor="#E0E8FF" p="1">
            <BatteryIcon boxSize="5" color="inherit" />
          </Box>

          {power.name}
        </Flex>

        {power.value && <PowerStats value={power.value} cap={power.cap} bgPos="right" color="#2a2e3a" />}
      </Flex>
    </RadioBox.Item>
  );
};
